import React, { useState } from "react";
import { useLocation, useNavigate, Navigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { postLoginRedirect } from "@/pages/public/Login";
import AuthShell from "@/layouts/AuthShell";
import { Button, Input, Label, toast } from "@/components/ds";

interface ChallengeState { challenge?: string; email?: string }

export default function MfaChallenge() {
  const location = useLocation();
  const navigate = useNavigate();
  const { finishLogin } = useAuth();
  const state = (location.state || {}) as ChallengeState;
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!state.challenge) return <Navigate to="/login" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await api.loginMfa(state.challenge as string, code.trim());
      if (res.token) {
        await finishLogin(res.token);
        toast.success("Signed in");
        navigate(await postLoginRedirect());
      }
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg); toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Two-factor verification" subtitle="Enter the 6-digit code from your authenticator app, or a backup code.">
      <form onSubmit={submit} className="space-y-4" data-testid="mfa-challenge-form">
        <div className="flex justify-center"><ShieldCheck className="h-10 w-10 text-blue-600" /></div>
        <div>
          <Label htmlFor="mfa-code">Verification code</Label>
          <Input id="mfa-code" autoFocus inputMode="text" placeholder="123456 or backup code"
            data-testid="mfa-challenge-code" value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
        {error && <p className="text-sm font-medium text-red-600" data-testid="mfa-challenge-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy || code.trim().length < 4} data-testid="mfa-challenge-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify
        </Button>
      </form>
      <div className="mt-4 text-center text-sm">
        <Link to="/login" className="text-blue-600 hover:text-blue-700" data-testid="mfa-back-to-login">Back to sign in</Link>
      </div>
    </AuthShell>
  );
}
